import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Leaf } from "lucide-react";
import Seo from "@/components/Seo";

export default function Login() {
  const { loginLocal, registerLocal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialMode = new URLSearchParams(location.search).get("mode") === "register" ? "register" : "login";
  const [tab, setTab] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [refCode, setRefCode] = useState("");

  useEffect(() => {
    const urlRef = new URLSearchParams(location.search).get("ref");
    const stored = localStorage.getItem("fz_ref");
    const code = (urlRef || stored || "").toUpperCase();
    if (code) {
      setRefCode(code);
      localStorage.setItem("fz_ref", code);
      setTab("register");
    }
  }, [location.search]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") {
        await loginLocal(form.email, form.password);
      } else {
        await registerLocal(form.email, form.name, form.password, refCode || null);
      }
      toast.success("Benvenuto in FreelanZen!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-zen-bg flex items-center justify-center px-6 py-12">
      <Seo
        title={tab === "register" ? "Registrati — FreelanZen" : "Accedi — FreelanZen"}
        description="Accedi o crea gratuitamente il tuo account FreelanZen: CRM, fatture, WhatsApp e solleciti AI per freelance italiani."
        path="/login"
        noIndex
      />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8" data-testid="logo-home-link">
          <img
            src="/brand/freelanzen-wordmark.png"
            alt="FreelanZen"
            className="h-10 w-auto object-contain"
          />
        </Link>

        <div className="zen-card p-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 mb-6 bg-zen-bg">
              <TabsTrigger value="login" data-testid="tab-login">Accedi</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Registrati</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <h2 className="font-heading text-2xl mb-1">Bentornato</h2>
              <p className="text-zen-ink2 text-sm mb-6">Accedi al tuo spazio di lavoro.</p>
            </TabsContent>
            <TabsContent value="register">
              <h2 className="font-heading text-2xl mb-1">Crea account</h2>
              <p className="text-zen-ink2 text-sm mb-6">In un minuto sei operativo.</p>
              {refCode && (
                <div data-testid="ref-banner" className="mb-4 p-3 rounded-xl bg-zen-primary/10 border border-zen-primary/20 text-sm text-zen-ink flex items-center gap-2">
                  <span className="text-lg">🎁</span>
                  <span>Ti stai registrando con l&apos;invito <b className="tracking-wider">{refCode}</b></span>
                </div>
              )}
            </TabsContent>

            <form onSubmit={onSubmit} className="space-y-4">
              {tab === "register" && (
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name" data-testid="input-name" required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Mario Rossi"
                    className="mt-1.5"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" data-testid="input-email" type="email" required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="tu@studio.it"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password" data-testid="input-password" type="password" required minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="min. 6 caratteri"
                  className="mt-1.5"
                />
              </div>
              <Button
                type="submit" disabled={loading} data-testid="submit-auth-btn"
                className="w-full bg-zen-primary hover:bg-zen-primaryHover text-white rounded-full py-6"
              >
                {loading ? "…" : tab === "login" ? "Accedi" : "Crea account"}
              </Button>
              {tab === "login" && (
                <div className="text-right">
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-zen-ink2 hover:text-zen-primary">
                    Password dimenticata?
                  </Link>
                </div>
              )}
            </form>

            <div className="flex items-center gap-3 my-6">
              <div className="h-px bg-zen-border flex-1" />
              <span className="text-xs text-zen-ink2 uppercase tracking-widest">oppure</span>
              <div className="h-px bg-zen-border flex-1" />
            </div>

            <Button
              type="button" onClick={googleLogin} data-testid="google-login-btn"
              variant="outline"
              className="w-full rounded-full py-6 border-zen-border hover:bg-zen-subtle/20"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" className="mr-2">
                <path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.5-.2-2.2H12v4.3h5.9c-.3 1.4-1 2.5-2.2 3.3v2.7h3.5c2-1.9 3.3-4.7 3.3-8.1z"/>
                <path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7L15.8 17.6c-1 .7-2.3 1.1-3.8 1.1-2.9 0-5.4-2-6.3-4.6H2.1v2.9C3.9 20.5 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.7 14.1c-.2-.7-.4-1.4-.4-2.1s.1-1.5.4-2.1V6.9H2.1C1.4 8.4 1 10.1 1 12s.4 3.6 1.1 5.1l3.6-3z"/>
                <path fill="#EA4335" d="M12 5.4c1.6 0 3.1.6 4.3 1.7l3.2-3.2C17.5 2.1 15 1 12 1 7.7 1 3.9 3.5 2.1 6.9l3.6 3C6.6 7.3 9.1 5.4 12 5.4z"/>
              </svg>
              Continua con Google
            </Button>
          </Tabs>
        </div>

        <p className="text-center text-sm text-zen-ink2 mt-6">
          <Link to="/" className="hover:text-zen-primary">← Torna alla home</Link>
        </p>
      </div>
    </div>
  );
}
